import { useContext } from 'react';

import { AuthContext } from './AuthContext';
import type { AuthContextValue, AuthPermissions, AuthUser } from './types';

/**
 * Main authentication hook
 *
 * Provides access to authentication state, user data, and authorization helpers.
 * Throws an error if used outside of AuthProvider to catch configuration bugs early.
 */
export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);

    if (context === null) {
        throw new Error(
            'useAuth must be used within an AuthProvider. ' +
                'Make sure AuthProvider is mounted higher in the component tree.'
        );
    }

    return context;
}

/**
 * Hook to get the current authenticated user
 *
 * Returns the user object if authenticated, null otherwise.
 * This is a convenience hook for components that only need user data.
 */
export function useCurrentUser(): AuthUser | null {
    const { user } = useAuth();
    return user;
}

/**
 * Hook to get authorization/permission helpers
 *
 * Returns functions to check user permissions and feature flags.
 * This is useful for components that only need to check permissions
 * without accessing the full auth context.
 *
 * @throws {Error} If used outside of AuthProvider
 */
export function usePermissions(): AuthPermissions {
    const {
        isOptoScaleAdmin,
        canViewAuditLog,
        isClientAdmin,
        hasFullClientAccess,
        isLocationAdmin,
        canAdministerLocation,
        hasUserFeature,
        canMoveBioscopes,
    } = useAuth();

    return {
        isOptoScaleAdmin,
        canViewAuditLog,
        isClientAdmin,
        hasFullClientAccess,
        isLocationAdmin,
        canAdministerLocation,
        hasUserFeature,
        canMoveBioscopes,
    };
}

/**
 * Hook to check if user is authenticated
 *
 * Returns authentication status. Useful for simple auth checks.
 *
 * @throws {Error} If used outside of AuthProvider
 */
export function useAuthStatus(): {
    isAuthenticated: boolean;
    isAuthenticating: boolean;
    error: Error | null;
} {
    const { isAuthenticated, isAuthenticating, error } = useAuth();

    return {
        isAuthenticated,
        isAuthenticating,
        error,
    };
}
